import {
  AudioLines,
  Disc,
  Hand,
  Keyboard,
  Link2,
  Maximize,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  MoreHorizontal,
  Presentation,
  Settings,
  SmilePlus,
  Users,
  Video,
  VideoOff,
} from 'lucide-react';
import { useState } from 'react';
import type { ComponentType, JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

import { cn } from '@/lib/utils';

import { REACTION_EMOJIS } from '../realtime/frames';
import { LeaveMenu } from './LeaveMenu';
import { QuickSettingsPanel, QuickSettingsPopover } from './QuickSettingsPopover';
import type { QuickSettingsActions, QuickSettingsState } from './QuickSettingsPopover';
import { RecordingControls } from './RecordingControls';
import type { RecordingControlsSnapshot } from './RecordingControls';
import { ToggleButton } from './ToggleButton';
import type { PanelMode } from './roomState';

/** The quick-settings binding: state + actions the room page owns (see QuickSettingsPopover). */
export type QuickSettingsBinding = QuickSettingsState & QuickSettingsActions;

/** Props the control bar needs from the room state; every toggle flips a real room concern. */
export interface ControlBarProps {
  readonly micEnabled: boolean;
  readonly camEnabled: boolean;
  readonly sharing: boolean;
  readonly screenBusy: boolean;
  /** Currently open side panel, or null when the panel is collapsed. */
  readonly panel: PanelMode | null;
  /** True while the collaborative board overlay covers the stage (the board button toggles it). */
  readonly boardOpen: boolean;
  /** Mobile layout: fixed bottom pill, icon-only buttons, reduced button set. */
  readonly isMobile: boolean;
  /** Own hand state, confirmed by the server (optimistic flip allowed). */
  readonly handRaised: boolean;
  /** Whether the reaction emoji picker is open. */
  readonly reactionsOpen: boolean;
  /** Unread chat frames received while the chat panel was closed; badge on the chat toggle. */
  readonly unreadChat: number;
  /** Connected participant count; badge on the participants toggle. */
  readonly participantsCount: number;
  /**
   * Lobby waiting count (CR-14); when above zero it REPLACES the participants badge so the
   * moderator reads «someone is held at the gate» at a glance. Staff-only data - the room page
   * omits the prop for everyone else and plain rooms keep the old badge.
   */
  readonly lobbyPendingCount?: number | undefined;
  /** True for moderators/admins: the recordings panel toggle renders in the «Ещё» menu. */
  readonly canRecordings: boolean;
  /**
   * The room's recording state (the unified bars): moderators get the start/stop control with
   * its live state dot right in the strip, everyone else sees the state indicator only.
   */
  readonly recording: RecordingControlsSnapshot | null;
  readonly recBusy: boolean;
  readonly onStartRec: () => void;
  readonly onStopRec: () => void;
  /**
   * True for moderators/admins: the destructive «Завершить для всех» leave option renders.
   * The control service enforces the same staff gate server-side.
   */
  readonly canEndForAll: boolean;
  /** True while the end-for-all kick loop is running (the option disables). */
  readonly endingForAll: boolean;
  /** True while the room root is in the browser fullscreen mode. */
  readonly isFullscreen: boolean;
  readonly onToggleHand: () => void;
  readonly onToggleReactions: () => void;
  /** Sends one allowlisted emoji; the picker closes after the pick. */
  readonly onSendReaction: (emoji: string) => void;
  readonly onToggleMic: () => void;
  readonly onToggleCam: () => void;
  readonly onToggleShare: () => void;
  readonly onTogglePanel: (target: PanelMode) => void;
  /** Opens or closes the collaborative board overlay (meetings). */
  readonly onToggleBoard: () => void;
  readonly onInvite: () => void;
  readonly onToggleFullscreen: () => void;
  readonly onOpenShortcuts: () => void;
  /** Opens the effects dialog (microphone processing + camera background). */
  readonly onOpenEffects: () => void;
  /**
   * The quick-settings binding (personal devices, camera quality, background mode, the effects
   * entry). Personal settings, so the gear renders for staff AND participants alike; absent on
   * surfaces that do not carry it (tests, surfaces without a room session).
   */
  readonly quickSettings?: QuickSettingsBinding | undefined;
  readonly onEndForAll: () => void;
  readonly onLeave: () => void;
}

/**
 * The Syncra floating control pill (the unified bottom bar): media toggles, invite, screen
 * share, the moderator's recording control with its live state dot, the reactions picker,
 * the hand toggle, panel toggles with badges, the board toggle (Доска overlay, ADR 0012), the
 * «Ещё» menu and the ONE red exit button (LeaveMenu: «Выйти» always, the destructive
 * «Завершить для всех» for moderators behind an inline confirm). Every toggle renders through
 * the shared ToggleButton primitive - pressed reads lit, released reads dimmed, no per-button
 * drift - every action is reachable by an aria-label, and hotkey-capable buttons carry the
 * `· K` suffix in their tooltip. On mobile the pill is fixed to the bottom edge with a compact
 * icon-only button set.
 */
export function ControlBar(props: ControlBarProps): JSX.Element {
  const { t } = useTranslation();
  const lobbyPendingCount = props.lobbyPendingCount ?? 0;
  const [moreOpen, setMoreOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  const micTooltip = props.micEnabled
    ? hotkeyLabel(t('controls.mute_mic'), 'M')
    : hotkeyLabel(t('controls.unmute_mic'), 'M');
  const camTooltip = props.camEnabled
    ? hotkeyLabel(t('controls.cam_off'), 'V')
    : hotkeyLabel(t('controls.cam_on'), 'V');
  const handTooltip = props.handRaised
    ? hotkeyLabel(t('controls.lower_hand'), 'H')
    : hotkeyLabel(t('controls.raise_hand'), 'H');

  return (
    <div
      className={cn(
        'flex shrink-0 justify-center border-t border-white/[0.06] px-3 pb-3 pt-2.5',
        // Desktop: `relative` makes this bar the picker's containing block. Mobile: `fixed`
        // already is one. The popover must anchor OUTSIDE the scrolling pill - `overflow-x-auto`
        // computes to clipping on both axes and used to cut the picker off (BR-2026-0928-1).
        props.isMobile ? 'fixed inset-x-0 bottom-0 z-40 bg-zinc-950/80 px-2 py-2' : 'relative',
      )}
    >
      {/* The reactions picker is a child of the bar, NOT of the pill below: the pill's
          overflow-x-auto would clip it (BR-2026-0928-1). Centered above the bar it escapes
          the scroll container's clip context while the buttons keep scrolling inside it. */}
      {props.reactionsOpen ? (
        <div
          data-testid="react-picker"
          role="menu"
          aria-label={t('controls.react_picker_aria')}
          className="absolute bottom-full left-1/2 z-30 mb-3 flex -translate-x-1/2 gap-1 rounded-lg border border-white/10 bg-zinc-900 p-2 shadow-xl shadow-black/50 backdrop-blur"
        >
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              role="menuitem"
              data-testid={`react-option-${emoji}`}
              aria-label={t('controls.react_one', { emoji })}
              title={t('controls.react_one', { emoji })}
              onClick={() => {
                props.onSendReaction(emoji);
              }}
              className="flex size-9 items-center justify-center rounded-lg text-lg transition-colors hover:bg-white/10"
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
      {/* BR-DM-4: the icon toggles live in their own shrinkable scroll zone; the leave
          split-button sits OUTSIDE it (shrink-0), so «Leave» stays fully visible and
          tappable at every width >= 320 - the row compresses (gap/width at <360px) and
          only the icon zone scrolls, with the thin dark scrollbar as the affordance. */}
      <div className="mx-auto flex w-fit max-w-full items-center gap-1 rounded-2xl border border-white/[0.08] bg-white/[0.04] p-1.5">
        <div className="scroll-thin-dark flex min-w-0 flex-1 items-center gap-1 overflow-x-auto max-[359px]:gap-0.5">
          <ControlButton
            testid="mic-toggle"
            label={props.micEnabled ? t('controls.mute_mic') : t('controls.unmute_mic')}
            tooltip={micTooltip}
            pressed={!props.micEnabled}
            danger={!props.micEnabled}
            compact={props.isMobile}
            onClick={props.onToggleMic}
            icon={props.micEnabled ? Mic : MicOff}
          />
          <ControlButton
            testid="cam-toggle"
            label={props.camEnabled ? t('controls.cam_off') : t('controls.cam_on')}
            tooltip={camTooltip}
            pressed={!props.camEnabled}
            danger={!props.camEnabled}
            compact={props.isMobile}
            onClick={props.onToggleCam}
            icon={props.camEnabled ? Video : VideoOff}
          />

          <PillSeparator compact={props.isMobile} />

          {!props.isMobile ? (
            <ControlButton
              testid="invite-button"
              label={t('controls.invite')}
              tooltip={t('controls.invite')}
              onClick={props.onInvite}
              icon={Link2}
            />
          ) : null}
          <ControlButton
            testid="share-screen"
            label={props.sharing ? t('controls.stop_share') : t('controls.share')}
            tooltip={props.sharing ? t('controls.stop_share') : t('controls.share')}
            pressed={props.sharing}
            accent={props.sharing}
            disabled={props.screenBusy}
            compact={props.isMobile}
            onClick={props.onToggleShare}
            icon={MonitorUp}
          />

          {/* The moderator's recording control with its live state dot (the unified bars);
              viewers and plain participants see the state indicator only. */}
          {props.recording !== null || props.canRecordings ? (
            <RecordingControls
              recording={props.recording}
              canControl={props.canRecordings}
              busy={props.recBusy}
              onStart={props.onStartRec}
              onStop={props.onStopRec}
            />
          ) : null}

          <ControlButton
            testid="react-button"
            label={t('controls.reactions')}
            tooltip={t('controls.reactions')}
            pressed={props.reactionsOpen}
            active={props.reactionsOpen}
            compact={props.isMobile}
            onClick={props.onToggleReactions}
            icon={SmilePlus}
          />

          <ControlButton
            testid="raise-hand"
            label={props.handRaised ? t('controls.lower_hand') : t('controls.raise_hand')}
            tooltip={handTooltip}
            pressed={props.handRaised}
            active={props.handRaised}
            amber={props.handRaised}
            compact={props.isMobile}
            onClick={props.onToggleHand}
            icon={Hand}
          />

          <PillSeparator compact={props.isMobile} />

          <ControlButton
            testid="panel-chat"
            label={t('controls.chat_panel')}
            tooltip={t('controls.chat_panel')}
            pressed={props.panel === 'chat'}
            active={props.panel === 'chat'}
            badge={props.unreadChat > 0 ? String(props.unreadChat) : undefined}
            compact={props.isMobile}
            onClick={() => {
              props.onTogglePanel('chat');
            }}
            icon={MessageSquare}
          />
          <ControlButton
            testid="panel-participants"
            label={t('controls.participants_panel')}
            tooltip={
              lobbyPendingCount > 0
                ? t('lobby.panel_title', { count: lobbyPendingCount })
                : t('controls.participants_panel')
            }
            pressed={props.panel === 'participants'}
            active={props.panel === 'participants'}
            badge={
              lobbyPendingCount > 0
                ? String(lobbyPendingCount)
                : props.participantsCount > 0
                  ? String(props.participantsCount)
                  : undefined
            }
            compact={props.isMobile}
            onClick={() => {
              props.onTogglePanel('participants');
            }}
            icon={Users}
          />
          {!props.isMobile ? (
            <ControlButton
              testid="board-button"
              label={t('controls.board')}
              tooltip={t('controls.board_tooltip')}
              pressed={props.boardOpen}
              active={props.boardOpen}
              onClick={props.onToggleBoard}
              icon={Presentation}
            />
          ) : null}

          {/* Quick settings (personal devices, quality, background, the effects entry): the
              gear renders for everyone - nothing behind it needs a role. The popover itself
              anchors to the bar container, NOT to this scroll zone (the BR-2026-0928-1 clip). */}
          {props.quickSettings !== undefined ? (
            <ControlButton
              testid="quick-settings"
              label={t('controls.quick_settings')}
              tooltip={t('controls.quick_settings')}
              pressed={quickOpen}
              active={quickOpen}
              compact={props.isMobile}
              onClick={() => {
                setQuickOpen((open) => !open);
              }}
              icon={Settings}
            />
          ) : null}

          {/* «Ещё» menu: the effects dialog (the old disabled noise-suppression placeholder,
            now real), the settings shortcut into the same quick-settings popover, the
            fullscreen toggle, the shortcuts dialog and the moderators' recordings panel. */}
          <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
            <ControlButton
              testid="more-menu"
              label={t('controls.more')}
              tooltip={t('controls.more')}
              pressed={moreOpen}
              active={moreOpen}
              compact={props.isMobile}
              icon={MoreHorizontal}
            >
              {(button) => <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>}
            </ControlButton>
            <DropdownMenuContent side="top" align="end" className="border-white/10 bg-zinc-900">
              <DropdownMenuItem
                data-testid="more-effects"
                className="gap-2 text-[12.5px]"
                onSelect={() => {
                  // BR-DM-3: the menu must not outlive the surface it opens (Radix closes on
                  // select; the explicit set keeps it deterministic when a dialog mounts).
                  setMoreOpen(false);
                  props.onOpenEffects();
                }}
              >
                <AudioLines className="size-3.5" />
                {t('controls.effects')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="more-settings"
                disabled={props.quickSettings === undefined}
                className="gap-2 text-[12.5px]"
                onSelect={() => {
                  if (props.quickSettings === undefined) {
                    return;
                  }
                  setMoreOpen(false);
                  setQuickOpen(true);
                }}
              >
                <Settings className="size-3.5" />
                {t('controls.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="fullscreen-toggle"
                className="gap-2 text-[12.5px]"
                onSelect={() => {
                  setMoreOpen(false);
                  props.onToggleFullscreen();
                }}
              >
                <Maximize className="size-3.5" />
                {props.isFullscreen ? t('controls.exit_fullscreen') : t('controls.fullscreen')}
              </DropdownMenuItem>
              {props.canRecordings ? (
                <DropdownMenuItem
                  data-testid="panel-recordings"
                  className="gap-2 text-[12.5px]"
                  onSelect={() => {
                    setMoreOpen(false);
                    props.onTogglePanel('recordings');
                  }}
                >
                  <Disc className="size-3.5" />
                  {t('controls.recordings_panel')}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                data-testid="shortcuts-menu-item"
                className="gap-2 text-[12.5px]"
                onSelect={() => {
                  setMoreOpen(false);
                  props.onOpenShortcuts();
                }}
              >
                <Keyboard className="size-3.5" />
                {t('controls.shortcuts')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <PillSeparator compact={props.isMobile} />

        {/* The ONE exit button (the unified room bars): the click opens the small menu -
            «Выйти» always, and for moderators the destructive end-for-all behind its
            inline confirm gate. Shared with the webinar bar through LeaveMenu. */}
        <LeaveMenu
          canEndForAll={props.canEndForAll}
          endingForAll={props.endingForAll}
          compact={props.isMobile}
          onLeave={props.onLeave}
          onEndForAll={props.onEndForAll}
        />
      </div>

      {/* The quick-settings surface is a child of the BAR container, not of the scrolling pill:
          overflow-x-auto computes to clipping on both axes and would cut it off (the reactions
          picker lesson, BR-2026-0928-1). Desktop: the compact popover, right-anchored above the
          bar. Mobile: the SAME panel inside a full-width bottom Sheet - a 292px floating box was
          cramped on phones and clipped off the top on short/landscape viewports, and its selects
          are awkward touch targets; the sheet adds scrim dismissal and grows them. */}
      {props.quickSettings !== undefined && quickOpen ? (
        props.isMobile ? (
          <Sheet open onOpenChange={setQuickOpen}>
            <SheetContent
              data-testid="quick-settings-sheet"
              side="bottom"
              hideClose
              closeLabel={t('quick.close_aria')}
              aria-describedby={undefined}
              className="items-stretch rounded-t-2xl border-white/10 bg-zinc-900 sm:max-w-none"
            >
              <SheetTitle className="sr-only">{t('controls.quick_settings')}</SheetTitle>
              <QuickSettingsPanel
                state={props.quickSettings}
                actions={props.quickSettings}
                onClose={() => {
                  setQuickOpen(false);
                }}
                variant="sheet"
              />
            </SheetContent>
          </Sheet>
        ) : (
          <div className="absolute right-0 bottom-full z-30 mb-2">
            <QuickSettingsPopover
              state={props.quickSettings}
              actions={props.quickSettings}
              onClose={() => {
                setQuickOpen(false);
              }}
            />
          </div>
        )
      ) : null}
    </div>
  );
}

/** Appends the latin hotkey hint to a tooltip: «Выключить микрофон · M». */
function hotkeyLabel(label: string, key: string): string {
  return `${label} · ${key}`;
}

interface ControlButtonProps {
  readonly testid: string;
  /** Accessible name; stays free of the hotkey suffix so labels stay stable. */
  readonly label: string;
  /** Tooltip text; usually the label plus the hotkey hint. */
  readonly tooltip?: string | undefined;
  /** The toggled-on state (pressed), even when the toggle means "off" for media. */
  readonly pressed?: boolean;
  readonly icon: ComponentType<{ className?: string }>;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  /** Solid red lit state: the media is currently off (muted mic, camera off). */
  readonly danger?: boolean;
  /** Solid emerald lit state: an accent state such as an active screen share. */
  readonly accent?: boolean;
  /** Solid amber lit state: the raised-hand toggle. */
  readonly amber?: boolean;
  /** The zinc-highlighted lit state (open panel, open menu) maps to the neutral primary. */
  readonly active?: boolean;
  /** Emerald count bubble on the top-right corner (unread chat, participant count). */
  readonly badge?: string | undefined;
  /** Mobile sizing: 36px instead of 40px. */
  readonly compact?: boolean;
  /**
   * Render override for wrapper components (dropdown triggers): receives the styled button
   * and must render it. When absent the button renders with its title tooltip.
   */
  readonly children?: (button: JSX.Element) => JSX.Element;
}

/**
 * One icon button of the Syncra control pill: a thin mapping onto the shared ToggleButton
 * primitive (the unified bars' lit/dim discipline) that keeps the pill's legacy prop names.
 */
function ControlButton(props: ControlButtonProps): JSX.Element {
  const variant = props.danger
    ? 'danger'
    : props.active && props.accent
      ? 'accent'
      : props.active && props.amber
        ? 'amber'
        : 'neutral';
  return (
    <ToggleButton
      testid={props.testid}
      label={props.label}
      tooltip={props.tooltip}
      icon={props.icon}
      pressed={props.pressed === true}
      onClick={props.onClick}
      disabled={props.disabled}
      variant={variant}
      badge={props.badge}
      compact={props.compact}
    >
      {props.children}
    </ToggleButton>
  );
}
function PillSeparator({ compact = false }: { readonly compact?: boolean }): JSX.Element | null {
  if (compact) {
    return null;
  }
  return <span aria-hidden className="mx-0.5 h-6 w-px bg-white/10" />;
}
